<script lang="ts">
	//Formating
	import { format } from 'svelte-i18n';
	//Design system components
	import { Text, Slider, Alert, TemperatureWheel, Input } from '@Shelly/shelly-design-system';
	// Helpers
	import { getErrorOrInfoMessageForMinAndMax, mathNormalize } from 'src/helpers/utilities';
	import { getDeviceStatus } from 'src/stores/device-runtime/status-selectors';
	import { getNumberInputConfig } from 'src/helpers/number_input_helpers';
	import { navigateToSetting } from 'src/stores/ui/navigate-to';
	import { bluTrvDefaultValue } from 'src/helpers/actions/local/devices/trv_local_actions';
	//Types
	import type { LocalControlOptions } from 'src/types/requests/schedules/schedules_g2';
	import type { Device } from 'src/types/Device';

	export let controlOption: LocalControlOptions, device: Device, errorInput: boolean;

	const valueRange = [4, 31];
	const step = 0.5;
	const btnStep = 0.5;

	$: errorInput = minMaxInfo?.error !== '' || String(controlOption?.params?.duration) === '';

	$: minMaxInfo = getErrorOrInfoMessageForMinAndMax(
		controlOption.params.duration,
		5,
		1440,
		false,
		true,
	);

	if (isNaN(controlOption.params.target_C)) {
		controlOption.params.target_C = bluTrvDefaultValue('target');
	}

	let pos = controlOption.params.hasOwnProperty('pos') ? controlOption.params.pos : 50;
	$: statusStore = getDeviceStatus(device.id);
	$: errorNotCalibrated = {
		show: $statusStore?.thermostats?.[0]?.pos == -1,
		label: $format('alert_device_not_calibrated', {
			default: 'Your device is not calibrated',
		}),
	};

	const min = -10,
		max = 10,
		units = 'C';

	controlOption.params.ext_C = mathNormalize(controlOption.params.ext_C, min, max);

	const a11yPropsSliderPos = {
		sliderValueText: $format('current_valve_position_value', {
			values: { value: pos || 0 },
			default: `Current valve position value is ${pos || 0}`,
		}),
		ariaLabel: $format('label_set_valve_target_position', {
			default: 'Set Valve Target Position',
		}),
		ariaLabelValue: $format('current_valve_position_value', {
			values: { value: pos || 0 },
			default: `Current valve position value is ${pos || 0}`,
		}),
	};

	const a11yPropsSliderPosOffset = {
		sliderValueText: $format('current_trv_offset_value', {
			values: { value: controlOption.params.ext_C || 0 },
			default: `Current TRV offset value is ${controlOption.params.ext_C || 0}`,
		}),
		ariaLabel: $format('label_set_trv_offset', { default: 'Set TRV Offset' }),
		ariaLabelValue: $format('current_trv_offset_value', {
			values: { value: controlOption.params.ext_C || 0 },
			default: `Current TRV offset value is ${controlOption.params.ext_C || 0}`,
		}),
	};

	const a11yProps = {
		tempWheelAriaLabel: $format('label_temperature', {
			default: 'Temperature',
		}),
		thumbAriaLabel: $format('label_temperature_thumb', {
			default: 'Temperature thumb',
		}),
		incrementBtnAriaLabel: $format('button_increase_temperature', {
			default: 'Increase temperature',
		}),
		decrementBtnAriaLabel: $format('button_decrease_temperature', {
			default: 'Decrease temperature',
		}),
	};
</script>

<div>
	{#if controlOption.attr?.pos != undefined}
		<Text class="mb-s" element="h2">
			{$format('label_valve_target_position', {
				default: 'Valve target position',
			})}
		</Text>
		{#if errorNotCalibrated.show}
			<Alert
				class="mb-s"
				heading={errorNotCalibrated.label}
				type="danger"
				cta={{
					title: $format('button_calibrate', {
						default: 'Calibrate',
					}),
				}}
				on:cta_click={() => navigateToSetting(device.id, 'settings', 'calibration')}
			/>
		{/if}
		<Slider
			id="slider-0cpcx3x"
			a11yProps={a11yPropsSliderPos}
			type="simple"
			value={pos}
			class="flex-1"
			disabled={errorNotCalibrated.show}
			on:value_change={(e) => {
				pos = e.detail.value;
				controlOption.params.pos = e.detail.value;
			}}
			title={$format('label_cover_slider_title', {
				values: { pos },
				default: 'Selected: {pos}%, Range: 0% (Fully closed) - 100 (Fully open)',
			})}
		/>
	{:else if controlOption.attr?.duration != undefined}
		<Text class="mb-s" element="h2">
			{$format('text_trv_setting_duration', {
				default: 'Duration',
			})}
		</Text>
		<Input
			size="m"
			value={controlOption.params.duration.toString()}
			label={$format('text_trv_setting_duration', {
				default: 'Duration',
			})}
			{.../^number/.test(controlOption.value) ? { ...getNumberInputConfig() } : {}}
			icon="fa fa-timer"
			tabIndex="-1"
			on:input={(e) => {
				controlOption.params.duration = e.target['value'].trim();
			}}
			info={minMaxInfo.info}
			error={minMaxInfo.error}
			id="input-xfraite"
			aria-labelledby="input-xfraite"
		/>
	{:else if controlOption.attr?.target_C != undefined}
		<TemperatureWheel
			{a11yProps}
			class="mb-s"
			valuePrefix="°C"
			value={controlOption.params.target_C}
			min={valueRange[0]}
			max={valueRange[1]}
			type={'temp'}
			{step}
			{btnStep}
			disabled={!controlOption.attr.target_C}
			on:value_change={(event) => {
				controlOption.params.target_C = event.detail.value;
			}}
		/>
	{:else if controlOption.attr?.ext_C != undefined}
		<Text styleType="body-small" class="mb-s">
			{$format('text_trv_offset_description', {
				values: {
					setting: $format(`the_temperature`, { default: 'the temperature' }),
					min,
					max,
					units,
				},
				default:
					'Offset in {units} to be applied to the measured temperature. The value must be between {min}°{units} and {max}°{units}.',
			})}
		</Text>
		<Slider
			id="slider-nbaffrp"
			a11yProps={a11yPropsSliderPosOffset}
			type="simple"
			unit={`°${units}`}
			{min}
			{max}
			bind:value={controlOption.params.ext_C}
		/>
	{/if}
</div>
